<template>
  <div class="vm-list-vue-results network-vm-results" @click="handlePaginationClick">
    <div class="network-vm-filter-panel">
      <div class="network-vm-filter-panel-title">Attached VM Filters</div>
      <div class="network-vm-filter-grid">
        <label class="network-vm-filter-field">
          <span class="network-vm-filter-label">Name</span>
          <input v-model="filters.name" type="text" class="form-control" placeholder="Filter VM names">
        </label>

        <label class="network-vm-filter-field">
          <span class="network-vm-filter-label">Status</span>
          <div ref="statusPickerRef" class="vm-search-select network-vm-filter-picker">
            <button
              type="button"
              class="form-control vm-search-select-trigger"
              :aria-expanded="statusPickerOpen ? 'true' : 'false'"
              @click.stop="toggleStatusPicker"
            >
              <span class="vm-search-select-trigger-label">{{ statusTriggerLabel }}</span>
              <span>{{ statusPickerOpen ? '▴' : '▾' }}</span>
            </button>

            <div v-if="statusPickerOpen" class="vm-search-select-panel" @click.stop>
              <button
                v-for="option in statusOptions"
                :key="`network-status-${option.value || 'all'}`"
                type="button"
                class="vm-search-select-option"
                @click="selectStatus(option.value)"
              >
                {{ option.label }}
              </button>
            </div>
          </div>
        </label>

        <label class="network-vm-filter-field">
          <span class="network-vm-filter-label">Owner</span>
          <input v-model="filters.owner" type="text" class="form-control" placeholder="Filter owners">
        </label>

        <label class="network-vm-filter-field">
          <span class="network-vm-filter-label">IPv4 / IPv6 Address</span>
          <input v-model="filters.address" type="text" class="form-control" placeholder="Filter IP addresses">
        </label>

        <label class="network-vm-filter-field">
          <span class="network-vm-filter-label">MAC Address</span>
          <input v-model="filters.mac" type="text" class="form-control" placeholder="Filter MAC addresses">
        </label>

        <div class="network-vm-filter-actions">
          <button type="button" class="btn btn-default vm-list-vue-filter-reset network-vm-filter-reset" @click="resetFilters">
            Reset Filters
          </button>
        </div>
      </div>
    </div>

    <div v-if="isLoading" class="network-results-overlay">
      <div class="network-results-loader">
        <span class="network-results-spinner" aria-hidden="true"></span>
        <span>Loading attached VMs...</span>
      </div>
    </div>

    <div class="vm-detail-shell" :id="shellId">
      <AppPagination
        :pagination="pagination"
        aria-label="Attached VM pagination"
        key-prefix="network-vm-page"
      />

      <div class="table-wrapper-scroll-y">
        <table class="table table-striped table-bordered table-condensed vm-detail-table-wrap">
          <thead>
            <tr>
              <th><div class="text-center">VM Name</div></th>
              <th><div class="text-center">Status</div></th>
              <th><div class="text-center">Owner</div></th>
              <th><div class="text-center">IP Address</div></th>
              <th><div class="text-center">MAC Address</div></th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="!filteredRows.length && !isLoading">
              <td colspan="5" class="text-center network-empty-state">No attached VMs found.</td>
            </tr>
            <tr v-for="row in paginatedRows" :key="`network-vm-${row.id}`">
              <td>
                <a
                  v-if="row.name?.can_open"
                  :href="row.name.detail_url?.replace(/\.json(\?|$)/, '$1') || '#'"
                  class="vm-detail-link"
                  @click.prevent.stop="handleOpenVmDetail(row.name.detail_url)"
                >
                  <AppOsIcon :symbol="row.name.os_icon_symbol" />
                  {{ row.name.text }}
                </a>
                <span v-else class="network-vm-name-cell">
                  <AppOsIcon :symbol="row.name?.os_icon_symbol" />
                  {{ row.name?.text }}
                </span>
              </td>
              <td class="text-center">
                <span class="app-status-chip" :class="row.state_class">{{ row.state_label }}</span>
              </td>
              <td class="text-center">{{ row.owner }}</td>
              <td class="text-center">
                <div class="network-vm-addresses">
                  <div v-for="address in row.ipv4 || []" :key="`v4-${row.id}-${address}`">{{ address }}</div>
                  <div v-for="address in row.ipv6 || []" :key="`v6-${row.id}-${address}`">{{ address }}</div>
                  <div v-if="!(row.ipv4 || []).length && !(row.ipv6 || []).length">N/A</div>
                </div>
              </td>
              <td class="text-center">{{ row.mac }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <aside
        :id="`${shellId}-panel`"
        class="vm-detail-panel"
        :style="vmDetailPanelStyle"
        :aria-hidden="vmDetail.open && vmDetail.mode === 'panel' ? 'false' : 'true'"
      >
        <VmDetailPanel
          :open="vmDetail.open && vmDetail.mode === 'panel'"
          :title="vmDetail.title"
          :detail-data="vmDetail.detailData"
          :ip-refresh-url="vmDetail.ipRefreshUrl"
          @close="closeVmDetail"
        />
      </aside>
      <VmDetailModal
        :modal-id="vmDetailModalId"
        :open="vmDetail.open && vmDetail.mode === 'modal'"
        :title="vmDetail.title"
        :detail-data="vmDetail.detailData"
        :ip-refresh-url="vmDetail.ipRefreshUrl"
        @close="closeVmDetail"
      />
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import AppPagination from './AppPagination.vue'
import AppOsIcon from './AppOsIcon.vue'
import VmDetailModal from './VmDetailModal.vue'
import VmDetailPanel from './VmDetailPanel.vue'

const props = defineProps({
  initialData: {
    type: Object,
    default: () => ({ rows: [], shell_id: 'network-vm-detail-shell' })
  },
  dataUrl: {
    type: String,
    default: ''
  },
  initialLoading: {
    type: Boolean,
    default: false
  }
})

const data = ref(props.initialData || { rows: [], shell_id: 'network-vm-detail-shell' })
const isLoading = ref(!!props.initialLoading)
const currentPage = ref(1)
const perPage = 20
const statusPickerOpen = ref(false)
const statusPickerRef = ref(null)
const filters = ref({
  name: '',
  status: '',
  owner: '',
  address: '',
  mac: ''
})

const shellId = computed(() => data.value?.shell_id || 'network-vm-detail-shell')
const vmDetail = ref({
  open: false,
  mode: 'panel',
  title: '',
  detailData: null,
  ipRefreshUrl: ''
})
const vmDetailPanelStyle = ref({})
const vmDetailModalId = computed(() => `show_network_vm_detail_${shellId.value}`)
const statusOptions = [
  { label: 'All statuses', value: '' },
  { label: 'UP', value: 'UP' },
  { label: 'STOP', value: 'STOP' },
  { label: 'PAUSE', value: 'PAUSE' },
  { label: 'UNKNOWN', value: 'UNKNOWN' }
]

function sccShowToast(level, message) {
  window.sccStatusShell?.sccShowToast?.(level, message)
}
const statusTriggerLabel = computed(() => statusOptions.find((option) => option.value === filters.value.status)?.label || 'All statuses')

function normalizeText(value) {
  return String(value || '').trim().toLowerCase()
}

function normalizeMac(value) {
  return String(value || '').replace(/[^0-9a-fA-F]/g, '').toLowerCase()
}

const filteredRows = computed(() => {
  const rows = data.value?.rows || []
  const nameKeyword = normalizeText(filters.value.name)
  const statusKeyword = normalizeText(filters.value.status)
  const ownerKeyword = normalizeText(filters.value.owner)
  const addressKeyword = normalizeText(filters.value.address)
  const macKeyword = normalizeMac(filters.value.mac)

  return rows.filter((row) => {
    if (nameKeyword && !normalizeText(row.name?.text).includes(nameKeyword)) return false
    if (statusKeyword && !normalizeText(row.state_label).includes(statusKeyword)) return false
    if (ownerKeyword && !normalizeText(row.owner).includes(ownerKeyword)) return false

    if (addressKeyword) {
      const allAddresses = [...(row.ipv4 || []), ...(row.ipv6 || [])].map((address) => normalizeText(address))
      if (!allAddresses.some((address) => address.includes(addressKeyword))) return false
    }

    if (macKeyword && !normalizeMac(row.mac).includes(macKeyword)) return false

    return true
  })
})

const pagination = computed(() => {
  const totalCount = filteredRows.value.length
  const totalPages = Math.max(1, Math.ceil(totalCount / perPage))
  const safeCurrentPage = Math.min(currentPage.value, totalPages)

  return {
    current_page: safeCurrentPage,
    total_pages: totalPages,
    prev_page: safeCurrentPage > 1 ? safeCurrentPage - 1 : null,
    next_page: safeCurrentPage < totalPages ? safeCurrentPage + 1 : null
  }
})

const paginatedRows = computed(() => {
  const safeCurrentPage = pagination.value.current_page
  const offset = (safeCurrentPage - 1) * perPage
  return filteredRows.value.slice(offset, offset + perPage)
})

function resetFilters() {
  filters.value = {
    name: '',
    status: '',
    owner: '',
    address: '',
    mac: ''
  }
  currentPage.value = 1
  statusPickerOpen.value = false
}

function handleOpenVmDetail(url) {
  if (!url) {
    return
  }

  requestJson(url, { method: 'GET' })
    .then((payload) => {
      const usePanel = shouldUseVmDetailPanel()
      const mode = usePanel ? 'panel' : 'modal'
      vmDetail.value = {
        open: true,
        mode,
        title: payload?.title || '',
        detailData: payload?.detail_data || null,
        ipRefreshUrl: payload?.ip_refresh_url || ''
      }

      const shell = document.getElementById(shellId.value)
      if (shell) {
        if (mode === 'panel') {
          shell.classList.add('vm-detail-shell-open')
          layoutVmDetailPanel()
        } else {
          shell.classList.remove('vm-detail-shell-open')
        }
      }
    })
    .catch((error) => {
      sccShowToast('danger', error?.message || 'Failed to load VM detail.')
    })
}

function shouldUseVmDetailPanel() {
  const shell = document.getElementById(shellId.value)
  const panel = document.getElementById(`${shellId.value}-panel`)
  if (!shell || !panel || window.innerWidth <= 991) {
    return false
  }

  const shellRect = shell.getBoundingClientRect()
  const availableRight = Math.max(0, window.innerWidth - shellRect.right - 18)
  return availableRight >= 280
}

function layoutVmDetailPanel() {
  const shell = document.getElementById(shellId.value)
  const table = shell ? shell.querySelector('.vm-detail-table-wrap') : null
  const footer = document.querySelector('.footer')
  const gap = 18
  if (!shell) {
    return
  }

  const shellRect = shell.getBoundingClientRect()
  const shellWidth = shell.offsetWidth
  const footerHeight = footer ? footer.offsetHeight : 0
  const viewportAvailableHeight = Math.max(320, window.innerHeight - Math.max(shellRect.top, 0) - footerHeight - gap)
  const panelHeight = Math.max(table ? table.offsetHeight : shell.offsetHeight, viewportAvailableHeight)

  if (window.innerWidth <= 991) {
    vmDetailPanelStyle.value = {
      left: '0px',
      width: `${shellWidth}px`,
      minHeight: `${panelHeight}px`,
      height: `${panelHeight}px`
    }
    return
  }

  const minPreferredWidth = 400
  const maxPreferredWidth = 560
  const availableRight = Math.max(0, window.innerWidth - shellRect.right - gap)
  const preferredWidth = Math.min(maxPreferredWidth, Math.max(minPreferredWidth, availableRight))
  let left = shellWidth + gap

  if (availableRight < preferredWidth) {
    left = Math.max(24, left - (preferredWidth - availableRight))
  }

  vmDetailPanelStyle.value = {
    left: `${left}px`,
    width: `${preferredWidth}px`,
    minHeight: `${panelHeight}px`,
    height: `${panelHeight}px`
  }
}

function closeVmDetail() {
  const shell = document.getElementById(shellId.value)
  if (shell) {
    shell.classList.remove('vm-detail-shell-open')
  }

  vmDetail.value = {
    open: false,
    mode: vmDetail.value.mode,
    title: '',
    detailData: null,
    ipRefreshUrl: ''
  }
}

function rerenderVmDetailForViewport() {
  if (!vmDetail.value.open) {
    return
  }

  const usePanel = shouldUseVmDetailPanel()
  const mode = usePanel ? 'panel' : 'modal'
  vmDetail.value = {
    ...vmDetail.value,
    mode
  }

  const shell = document.getElementById(shellId.value)
  if (shell) {
    if (mode === 'panel') {
      shell.classList.add('vm-detail-shell-open')
      layoutVmDetailPanel()
    } else {
      shell.classList.remove('vm-detail-shell-open')
    }
  }
}

function selectStatus(value) {
  filters.value = {
    ...filters.value,
    status: value
  }
  statusPickerOpen.value = false
}

function toggleStatusPicker() {
  statusPickerOpen.value = !statusPickerOpen.value
}

function closePickersOnOutsideClick(event) {
  if (!statusPickerRef.value || statusPickerRef.value.contains(event.target)) {
    return
  }

  statusPickerOpen.value = false
}

function handlePaginationClick(event) {
  const link = event.target.closest('.pagination a[data-page]')
  if (!link) {
    return
  }

  event.preventDefault()
  const page = Number(link.dataset.page)
  if (!Number.isFinite(page) || page < 1) {
    return
  }

  currentPage.value = page
}

function mountNestedVue() {
  if (window.sccMountVueComponents) {
    window.sccMountVueComponents()
  }
}

async function requestJson(url, options = {}) {
  const method = (options.method || 'GET').toUpperCase()
  const headers = {
    Accept: 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
    ...(options.headers || {})
  }
  const fetchOptions = {
    method,
    headers,
    credentials: 'same-origin'
  }

  const response = await fetch(url, fetchOptions)
  const text = await response.text()
  let payload = null

  if (text) {
    try {
      payload = JSON.parse(text)
    } catch {
      payload = null
    }
  }

  if (!response.ok) {
    throw new Error((payload && payload.message) || text || 'Request failed.')
  }

  return payload
}

async function loadRows() {
  if (!props.dataUrl) {
    return
  }

  isLoading.value = true
  const startedAt = Date.now()

  try {
    const response = await fetch(props.dataUrl, {
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      credentials: 'same-origin'
    })

    if (!response.ok) {
      throw new Error(`Request failed with status ${response.status}`)
    }

    const payload = await response.json()
    data.value = payload || { rows: [], shell_id: shellId.value }
  } finally {
    const elapsed = Date.now() - startedAt
    if (elapsed < 250) {
      await new Promise((resolve) => window.setTimeout(resolve, 250 - elapsed))
    }
    isLoading.value = false
  }
}

onMounted(async () => {
  document.addEventListener('click', closePickersOnOutsideClick)
  document.addEventListener('keyup', handleDocumentKeyup)
  window.addEventListener('resize', rerenderVmDetailForViewport)
  nextTick(mountNestedVue)
  try {
    await loadRows()
  } catch (error) {
    console.error('Failed to load network attached VMs', error)
    window.alert('Failed to load attached VMs.')
  }
})

onBeforeUnmount(() => {
  document.removeEventListener('click', closePickersOnOutsideClick)
  document.removeEventListener('keyup', handleDocumentKeyup)
  window.removeEventListener('resize', rerenderVmDetailForViewport)
})

watch(filters, () => {
  currentPage.value = 1
}, { deep: true })

function handleDocumentKeyup(event) {
  if (event.key !== 'Escape') {
    return
  }

  closeVmDetail()
}
</script>

<style scoped>
.network-vm-results {
  position: relative;
}

.network-vm-filter-panel {
  margin-bottom: 18px;
  padding: 18px 20px;
  border: 1px solid #d9e2ec;
  border-radius: 18px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.94), rgba(241, 245, 249, 0.96));
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.06);
}

.network-vm-filter-panel-title {
  color: #0f172a;
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 14px;
}

.network-vm-filter-grid {
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
  align-items: end;
}

.network-vm-filter-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.network-vm-filter-picker {
  position: relative;
}

.network-vm-filter-label {
  color: #475569;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.network-vm-filter-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.network-vm-filter-reset {
  min-width: 120px;
  min-height: 42px;
  padding: 10px 16px;
  border-radius: 12px;
  border: 1px solid #cbd5e1;
  background: linear-gradient(180deg, #ffffff 0%, #eef2f7 100%);
  color: #0f172a;
  font-weight: 600;
  box-shadow: 0 8px 18px rgba(15, 23, 42, 0.08);
}

.network-vm-filter-reset:hover,
.network-vm-filter-reset:focus {
  color: #0f172a;
  background: linear-gradient(180deg, #ffffff 0%, #e2e8f0 100%);
  border-color: #94a3b8;
  box-shadow: 0 0 0 3px rgba(148, 163, 184, 0.16);
}

.network-vm-addresses {
  display: inline-flex;
  flex-direction: column;
  gap: 2px;
  align-items: center;
}

.network-results-overlay {
  position: absolute;
  inset: 0;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(248, 250, 253, 0.82);
  backdrop-filter: blur(1px);
}

.network-results-loader {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  padding: 14px 18px;
  border: 1px solid rgba(130, 150, 175, 0.24);
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.96);
  color: #36506a;
  font-size: 13px;
  font-weight: 600;
  box-shadow: 0 16px 34px rgba(122, 141, 164, 0.16);
}

.network-results-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(116, 143, 171, 0.22);
  border-top-color: #4d6d8f;
  border-radius: 999px;
  animation: network-vm-spin 0.9s linear infinite;
}

.network-empty-state {
  color: #64748b;
  padding: 24px 12px !important;
}

@keyframes network-vm-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
